"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn, spacingClassesHero, sectionBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero5Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    image?: string;
    imageAltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  hasFeaturedItems?: boolean;
  featuredItemsWithDarkIconBg?: boolean;
  featuredItems?: {
    icon?: string;
    title?: string;
    content?: string;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero5 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  hasFeaturedItems,
  featuredItemsWithDarkIconBg,
  featuredItems,
  attributes,
}: Hero5Props) => {
  const spacingClass = spacingClassesHero[spacing || 'none'];

  // This is the background color for the hero section when half/half background is required
  // hide this for now since we are using the pageNav
  // const specialBackgroundClass = () => {
  //   if (darkMode) {
  //       return "bg-white";
  //   } else {
  //     switch (variant) {
  //       case "A":
  //         return "bg-muted";
  //       case "B":
  //         return "bg-white";
  //       default:
  //         return "bg-muted";
  //     }
  //   }
  // }

  const heroClass = cn(
    spacingClass,
    sectionBackgroundColors(darkMode, variant),
    // hasFeaturedItems && '!pb-20',
  );

  const featuredContainerClass = cn(
    spacingClass,
    sectionBackgroundColors(darkMode, variant),
    // specialBackgroundClass(),
    "!pt-0",
  );

  // const featuredItemsBackgroundClass = cn(
  //   sectionBackgroundColors(darkMode, variant),
  //   "absolute inset-0 top-0 left-0 right-0 h-30 lg:h-1/2 z-0",
  // );

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero5"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 5"
      className="!p-0 !bg-transparent"
    > 
    
      <div 
        className={heroClass}
      >
        <div className="container"> 
          <div className="flex flex-col lg:flex-row gap-20 lg:gap-24">
            <div className="w-full lg:w-12/24 flex items-center">
              <div className="flex flex-col gap-6 text-center lg:text-left">
                {contents?.badge && (
                  <Badge variant="outline" bold className="mx-auto lg:mx-0">
                    {contents?.badge}
                  </Badge>
                )}
                {contents?.title && (
                  <h1>
                    {parse(contents.title)}
                  </h1>
                )}
                {contents?.content && (
                  <div className="mt-4 paragraph-lg">
                    {parse(contents?.content)}
                  </div>
                )}
                {contents?.hasButton && (
                  <div className="mt-2">
                    <div className="flex flex-col items-center gap-4 lg:flex-row">
                      <Button
                        asChild
                        size='lg'
                        className="w-full sm:w-auto"
                        variant={contents?.buttonStyle || "primary"}
                      >
                        <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                      </Button>
                      {contents?.secondaryButton && (
                        <Button
                          className="w-full sm:w-auto"
                          variant={contents?.secondaryButtonStyle || "outline"}
                          asChild
                          size='lg'
                        >
                          <Link href={contents?.secondaryButtonLink ?? ""}> 
                            {contents?.secondaryButtonText}
                          </Link>
                        </Button>
                      )}
                    </div>
                    {contents?.microCopy && (
                      <p className="mt-2 paragraph-sm text-muted-foreground">
                        {contents?.microCopy}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="w-full lg:w-12/24 flex items-center justify-center rounded-xl sm:rounded-2xl xl:rounded-3xl">
              {contents?.image && (
                <Image
                  src={contents?.image}
                  alt={contents?.imageAltText || ''}
                  title={contents?.imageAltText || ''}
                  width={688}
                  height={521}
                  className="object-cover"
                  priority
                  fetchPriority="high"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {hasFeaturedItems && (
        <div className={featuredContainerClass}>
          <div className="relative">
            <div className="container relative z-10 light">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 rounded-lg bg-background shadow-xl">
                {featuredItems?.map((item, index) => (
                  <div key={index} className={
                    cn(
                      "relative flex md:block p-6 lg:border-b-0", 
                      (index !== 3) && "border-b md:border-b-0",
                      (index !== 0 && index !== 2) && "md:border-l",
                      (index !== 2 && index !== 3) && "md:border-b",
                      (index !== 0) && "lg:border-l",
                    )}
                  >
                    <div className="flex flex-col gap-4 md:gap-16">
                      <div className={cn("rounded-full p-3 size-12 ", featuredItemsWithDarkIconBg ? "bg-gray-800" : "bg-accent")}>
                        <span className="material-symbols-outlined text-brand">
                          {item.icon}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        {item.title && (
                          <div className="paragraph-lg font-bold text-foreground">
                            {item.title}
                          </div>
                        )}
                        {item.content && (
                          <div className="paragraph-base text-muted-foreground">
                            {parse(item.content)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* <div className={featuredItemsBackgroundClass}></div> */}
          </div>
        </div>
      )}
    </SectionWrapper>
  );
};

export { Hero5 };
